import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { OdooToastService } from 'src/app/services/odoo/odoo-toast.service';
import { conCarga, mensajeDeFallo } from 'src/app/services/odoo/odoo.service';
import {
  ArchivoDia, ClaseArchivo, DetalleDia, FiltroDetalle, HistorialDia, OdooArchivosService,
  diaCorto, diaLargo, momento, momentoLargo, num, usd,
} from 'src/app/services/odoo/odoo-archivos.service';

type Pestana = ClaseArchivo | 'historial';
interface Columna { i: number; titulo: string; num: boolean; }
interface Kpi { titulo: string; monto: string; detalle: string; total: boolean; }
interface Evento { texto: string; cuando: string; motivo: string | null; error: string | null; }
interface VistaDia {
  cargando: boolean;
  error: string | null;
  datos: DetalleDia | null;
  titulo: string;
  nGiros: number | string;
  nPagos: number | string;
  kpis: Kpi[];
  comercios: string[];
  cols: Columna[];
  filas: string[][];
  eventos: Evento[];
  ultimo: string;
  subidoISO: string;
  yaSubido: boolean;
  aviso: string;
  hayArchivo: boolean;
}

const COLUMNAS_CLAVE = [/^credito/, /^cedula/, /^comercio/, /^agencia$/, /^referencia/, /^metodo de pago$/, /^monto/, /^fecha (de )?(vencimiento|valor)/];
const sinTildes = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/**
 * Detalle de un día: «Ver» (lee el xlsx guardado en el buzón) o «Generar y ver» (vista previa en memoria, no sube nada).
 * Pestañas Giros · N / Pagos · M / Historial, totales por comercio, buscador por cédula o crédito, filtro de comercio y
 * tabla paginada en el servidor.
 */
@Component({
  selector: 'app-odoo-archivo-dia',
  templateUrl: './odoo-archivo-dia.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OdooArchivoDiaComponent implements OnChanges, OnDestroy {
  @Input() fecha = '';
  @Input() previa = false;
  @Input() puedeEditar = false;
  @Input() version = 0;
  @Input() subiendo = false;
  @Output() volver = new EventEmitter<void>();
  @Output() regenerar = new EventEmitter<{ fecha: string; subidoISO: string }>();
  @Output() subir = new EventEmitter<{ fecha: string; yaSubido: boolean; subidoISO: string }>();

  pestana: Pestana = 'creditos';
  texto = '';
  todasColumnas = false;
  descargando = false;
  readonly esperando = [0, 1, 2, 3, 4];

  private readonly filtro$ = new BehaviorSubject<FiltroDetalle>({ clase: 'creditos', q: '', comercio: '', page: 1, limit: 10 });
  private readonly escribe$ = new Subject<string>();
  private readonly sub: Subscription;
  readonly vm$: Observable<VistaDia> = conCarga(this.filtro$, (f) => this.api.detalle(this.fecha, f, this.previa)).pipe(
    map((c) => this.armar(c.cargando, c.error, c.datos)),
  );

  constructor(private api: OdooArchivosService, private toast: OdooToastService, private cdr: ChangeDetectorRef) {
    this.sub = this.escribe$.pipe(debounceTime(300), distinctUntilChanged()).subscribe((q) => this.cambiar({ q, page: 1 }));
  }

  ngOnChanges(c: SimpleChanges): void {
    // Cambió la versión (se subió o regeneró) o pasó de vista previa a día subido: se vuelve a pedir.
    const resto = (c['version'] && !c['version'].firstChange) || (c['previa'] && !c['previa'].firstChange);
    if (resto) this.cambiar({ page: 1 });
  }
  ngOnDestroy(): void { this.sub.unsubscribe(); }

  private cambiar(p: Partial<FiltroDetalle>): void { this.filtro$.next({ ...this.filtro$.value, ...p }); }

  // ── Armado ──
  private armar(cargando: boolean, error: string | null, d: DetalleDia | null): VistaDia {
    const vacio: VistaDia = {
      cargando, error, datos: null, titulo: diaLargo(this.fecha), nGiros: '', nPagos: '', kpis: [], comercios: [], cols: [], filas: [], eventos: [],
      ultimo: '', subidoISO: '', yaSubido: false, aviso: '', hayArchivo: false,
    };
    if (!d) return vacio;
    const clase = d.clase;
    const tot = d.totales[clase];
    const unidad = clase === 'creditos' ? 'cuotas' : 'abonos';
    const kpis: Kpi[] = tot ? [
      ...tot.comercios.map((c) => ({ titulo: c.comercio, monto: usd(c.monto), detalle: `${c.creditos} ${c.creditos === 1 ? 'crédito' : 'créditos'} · ${c.filas} ${unidad}`, total: false })),
      { titulo: 'Total del día', monto: usd(tot.monto), detalle: `${tot.creditos} ${tot.creditos === 1 ? 'crédito' : 'créditos'} · ${tot.filas} ${unidad}`, total: true },
    ] : [];
    const cols = this.columnas(d.encabezados);
    const archivos = [d.registros.creditos, d.registros.pagos].filter((a): a is ArchivoDia => !!a && a.estado !== 'error');
    const subidoISO = archivos.map((a) => a.subido_en || a.updatedAt || '').filter(Boolean).sort().pop() || '';
    const eventos = d.historial.map((h) => this.evento(h));
    return {
      ...vacio, datos: d, kpis, cols,
      nGiros: d.totales.creditos ? d.totales.creditos.filas : 0, nPagos: d.totales.pagos ? d.totales.pagos.filas : 0,
      comercios: tot ? tot.comercios.map((c) => c.comercio) : [],
      filas: d.items.map((r) => cols.map((c) => (c.num ? this.monto(r[c.i]) : String(r[c.i] ?? '')))),
      eventos, ultimo: eventos.length ? this.resumenUltimo(d.historial[0]) : '',
      subidoISO, yaSubido: archivos.length > 0, hayArchivo: !!tot,
      aviso: this.previa
        ? `Vista previa del ${diaCorto(this.fecha)}. ${archivos.length ? `Ese día ya se subió el ${momentoLargo(subidoISO)}; subirlo de nuevo pide un motivo.` : 'Todavía no se ha subido nada.'}`
        : '',
    };
  }

  private monto(v: string | number): string {
    const n = Number(v);
    return v === '' || v === undefined || isNaN(n) ? String(v ?? '') : new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  }

  /** Columnas principales según los encabezados del propio archivo (así sigue sirviendo si el formato cambia). */
  private columnas(enc: string[]): Columna[] {
    const todas = enc.map((h, i) => ({ i, titulo: h.replace(/\s*\(.*?\)\s*/g, ' ').replace(/\s+/g, ' ').trim(), num: /^monto/.test(sinTildes(h)) }));
    if (this.todasColumnas) return todas;
    const claves = todas.filter((c) => COLUMNAS_CLAVE.some((re) => re.test(sinTildes(enc[c.i]))));
    return claves.length >= 3 ? claves : todas;
  }

  private por(h: HistorialDia): string { return h.origen === 'cron' ? 'el proceso automático' : h.usuario || 'un usuario'; }
  private accion(h: HistorialDia): string { return h.accion === 'regenerado' ? 'regenerado y subido' : h.accion === 'subido' ? 'generado y subido' : 'no se pudo subir'; }
  private resumenUltimo(h: HistorialDia): string { return `${this.accion(h)} el ${momentoLargo(h.fecha)} por ${this.por(h)}.`; }
  private evento(h: HistorialDia): Evento {
    const que = h.clase === 'pagos' ? 'Pagos' : h.clase === 'creditos' ? 'Giros' : '';
    const a = this.accion(h);
    return {
      texto: `${que ? que + ': ' : ''}${a.charAt(0).toUpperCase() + a.slice(1)} por ${this.por(h)}`,
      cuando: momento(h.fecha), motivo: h.motivo, error: h.error,
    };
  }

  // ── Acciones ──
  elegir(p: Pestana): void {
    if (p === this.pestana) return;
    this.pestana = p;
    if (p !== 'historial') { this.texto = ''; this.cambiar({ clase: p, q: '', comercio: '', page: 1 }); }
  }
  escribir(v: string): void { this.texto = v; this.escribe$.next(v.trim()); }
  filtrarComercio(v: string): void { this.cambiar({ comercio: v, page: 1 }); }
  ir(n: number): void { this.cambiar({ page: n }); }
  alternarColumnas(): void { this.todasColumnas = !this.todasColumnas; this.cambiar({}); }
  totalPaginas(total: number, limit: number): number { return Math.max(1, Math.ceil(total / limit)); }
  recargar(): void { this.cambiar({}); }

  descargar(vm: VistaDia): void {
    const clase = this.pestana === 'historial' ? this.filtro$.value.clase : this.pestana;
    const a = vm.datos ? vm.datos.registros[clase] : null;
    if (!a || this.descargando) return;
    this.descargando = true;
    this.api.descargar(a._id).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const e = document.createElement('a');
        e.href = url; e.download = a.nombre; e.click();
        URL.revokeObjectURL(url);
        this.descargando = false; this.cdr.markForCheck();
      },
      error: (e: unknown) => { this.descargando = false; this.toast.mostrar(mensajeDeFallo(e), false); this.cdr.markForCheck(); },
    });
  }

  claseActiva(): ClaseArchivo { return this.pestana === 'historial' ? this.filtro$.value.clase : this.pestana; }
  porIndice = (i: number): number => i;
  porTitulo = (_: number, k: Kpi): string => k.titulo;
  readonly numero = num;
}
